<template>
  <Modal ref="modalRef" @after-close="clearForm">
    <template #header>Create new agent</template>

    <template #body>
      <FormGroup class="form">
        <FormField label="First Name" :error="form.fieldErrors('firstName')">
          <Input v-model="form.fields.firstName" />
        </FormField>

        <FormField label="Last Name" :error="form.fieldErrors('lastName')">
          <Input v-model="form.fields.lastName" />
        </FormField>

        <FormField label="Phone number" :error="form.fieldErrors('phone')">
          <InputPhone v-model="form.fields.phone" />
        </FormField>
      </FormGroup>
    </template>

    <template #footer>
      <Button variant="ghost" @click="modalRef?.close">Cancel</Button>
      <Button @click="save" :loading="form.submitting">Create</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { helpers, required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { ref } from 'vue';

import {
  phoneNumberValidator,
  Button,
  FormField,
  FormGroup,
  Input,
  InputPhone,
  Modal,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import { normalizePhoneNumber } from '@tallo/utility';

import { SaveShowingAgentDto } from '../interfaces/save-showing-agent.dto';
import { ShowingAgentDto } from '../interfaces/showing-agent.dto';
import { showingAgentsQueryKey } from '../queries/showing-agents.query';
import showingAgentsService from '../services/showing-agents.service';

/** Given a unit, the new agent is created on it (and on the rest of its portfolio) rather than only on the roster */
const props = defineProps<{ propertyId?: string }>();
const emit = defineEmits<{ created: [showingAgent: ShowingAgentDto] }>();

const queryCache = useQueryCache();
const alertsStore = useAlertsStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const initialFormValue = {
  firstName: '',
  lastName: '',
  phone: '',
};

const form = useForm<SaveShowingAgentDto>(initialFormValue, {
  firstName: {
    required: helpers.withMessage('First name is required', required),
  },
  lastName: {
    required: helpers.withMessage('Last name is required', required),
  },
  phone: {
    phoneNumberValidator,
    required: helpers.withMessage('Phone number is required', required),
  },
});

async function save() {
  try {
    await form.submit(async () => {
      const showingAgent = props.propertyId
        ? await createOnUnit(props.propertyId)
        : await showingAgentsService.create(form.fields);

      await queryCache.invalidateQueries({ key: [showingAgentsQueryKey] });

      modalRef.value?.close();

      if (showingAgent) {
        emit('created', showingAgent);
      }
    });
  } catch (error) {
    // A rejected field lands on the field itself; anything else from the server is a toast
    if (error instanceof AxiosError && !error.response?.data?.validationErrors) {
      alertsStore.catchError(error);
    }
  }
}

/** The unit answers with its whole list; the new agent is the one with this number, adopted if the roster already had it */
async function createOnUnit(propertyId: string): Promise<ShowingAgentDto | undefined> {
  const list = await showingAgentsService.addToProperty(propertyId, { ...form.fields });
  const phone = normalizePhoneNumber(form.fields.phone);

  return list.agents.find((agent) => normalizePhoneNumber(agent.phone) === phone);
}

function clearForm() {
  Object.assign(form.fields, { ...initialFormValue });
}

defineExpose({ modalRef });
</script>

<style scoped lang="scss">
.form {
  width: 27rem;
  max-width: 100%;
}
</style>
